import { useCallback, useState } from 'react';

/**
 * Tracks per-action busy state so any button can show a loader while its async
 * handler runs and auto-clear when it settles (success OR error). Used across the
 * Moderators Availability views (Save / Approve / Delete / Reject) — OD-980.
 *
 * Usage:
 *   const { run, isBusy } = useActionLoading();
 *   <Button disabled={isBusy('approve:'+id)}
 *           onClick={() => run('approve:'+id, () => api.approve(id))}>
 *     {isBusy('approve:'+id) ? <LoaderSpinner size="sm" /> : 'Approve'}
 *   </Button>
 *   {isBusy() && <PageLoader overlay />}   // any action in flight
 */
export function useActionLoading() {
  const [busyMap, setBusyMap] = useState<Record<string, boolean>>({});

  const run = useCallback(
    async <T>(key: string, fn: () => Promise<T>): Promise<T> => {
      setBusyMap((prev) => ({ ...prev, [key]: true }));
      try {
        return await fn();
      } finally {
        setBusyMap((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }
    },
    [],
  );

  /** With a key: is that action busy. Without: is ANY action busy. */
  const isBusy = useCallback(
    (key?: string): boolean =>
      key ? Boolean(busyMap[key]) : Object.values(busyMap).some(Boolean),
    [busyMap],
  );

  return { run, isBusy };
}
